import {
  LayoutDashboard,
  Users,
  DollarSign,
  Activity,
  Shield,
  Settings,
  Flag,
  Mail,
  Server,
  Heart,
  HeartPulse,
  HelpCircle,
  Bell,
  BarChart3,
  Database,
  MessageSquare,
  Key,
  Gift,
  Ticket,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

export interface AdminNavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  description?: string;
  badge?: string;
}

export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

// Core navigation items
export const coreNavItems: AdminNavItem[] = [
  {
    title: "Overview",
    url: "/admins/dashboard",
    icon: LayoutDashboard,
    description: "Dashboard overview and key metrics",
  },
  {
    title: "Users",
    url: "/admins/users",
    icon: Users,
    description: "Manage all platform users",
  },
  {
    title: "Revenue",
    url: "/admins/revenue",
    icon: DollarSign,
    description: "Revenue and subscription analytics",
  },
  {
    title: "Business Metrics",
    url: "/admins/business-metrics",
    icon: TrendingUp,
    description: "Advanced business analytics",
  },
];

// Monitoring items
export const monitoringNavItems: AdminNavItem[] = [
  {
    title: "System Status",
    url: "/admins/system-status",
    icon: Server,
    description: "External service health",
  },
  {
    title: "Monitoring",
    url: "/admins/monitoring",
    icon: Activity,
    description: "API usage monitoring",
  },
  {
    title: "Security Audit",
    url: "/admins/security",
    icon: Shield,
    description: "Security logs and audit trail",
  },
  {
    title: "Data Retention",
    url: "/admins/data-retention",
    icon: Database,
    description: "Data cleanup policies",
  },
  {
    title: "System Health",
    url: "/admins/health",
    icon: Heart,
    description: "Monitor system performance",
  },
];

// Platform items
export const platformNavItems: AdminNavItem[] = [
  {
    title: "Analytics",
    url: "/admins/analytics",
    icon: BarChart3,
    description: "Platform analytics",
  },
  {
    title: "Platform Feedback",
    url: "/admins/feedback",
    icon: MessageSquare,
    description: "User feedback on platform",
  },
  {
    title: "Notifications",
    url: "/admins/notifications",
    icon: Bell,
    description: "Admin notification settings",
  },
  {
    title: "Email Logs",
    url: "/admins/email-logs",
    icon: Mail,
    description: "Email delivery tracking",
  },
];

// Features items
export const featureNavItems: AdminNavItem[] = [
  {
    title: "Feature Flags",
    url: "/admins/feature-flags",
    icon: Flag,
    description: "Control feature rollouts",
  },
  {
    title: "API Keys",
    url: "/admins/api-keys",
    icon: Key,
    description: "Manage API keys",
  },
  {
    title: "Referrals",
    url: "/admins/referrals",
    icon: Gift,
    description: "Referral program management",
  },
];

// Customer success items
export const customerSuccessNavItems: AdminNavItem[] = [
  {
    title: "Support Tickets",
    url: "/admins/support",
    icon: Ticket,
    description: "Customer support tickets",
  },
  {
    title: "Customer Health",
    url: "/admins/customer-health",
    icon: HeartPulse,
    description: "Customer health scores",
  },
];

// Settings items
export const settingsNavItems: AdminNavItem[] = [
  {
    title: "Settings",
    url: "/admins/settings",
    icon: Settings,
    description: "Admin preferences",
  },
  {
    title: "Help",
    url: "/admins/help",
    icon: HelpCircle,
    description: "Support and resources",
  },
];

// All navigation groups for sidebar
export const adminNavGroups: AdminNavGroup[] = [
  { label: "Core", items: coreNavItems },
  { label: "Monitoring", items: monitoringNavItems },
  { label: "Platform", items: platformNavItems },
  { label: "Features", items: featureNavItems },
  { label: "Customer Success", items: customerSuccessNavItems },
  { label: "Settings", items: settingsNavItems },
];

// Mobile bottom navigation items (subset)
export const mobileBottomNavItems: AdminNavItem[] = [
  {
    title: "Overview",
    url: "/admins/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Health",
    url: "/admins/health",
    icon: Activity,
  },
];

// Mobile "More" navigation groups - uses main nav groups for consistency
export const mobileMoreNavGroups: AdminNavGroup[] = adminNavGroups;

// Helper function to check if a path is active
export function isAdminPathActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === "/admins") {
    return currentPath === "/admins";
  }
  return currentPath.startsWith(itemPath);
}

// Get all admin routes for command palette
export function getAllAdminRoutes(): AdminNavItem[] {
  return adminNavGroups.flatMap(group => group.items);
}
